import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { listUsers, createUser, updateUser, resetUserPassword, ROLE_LABELS } from '../lib/accountService';
import { fetchRoster } from '../lib/routingService';

const ROLES = Object.keys(ROLE_LABELS);

function errText(err, fallback) {
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
}

export default function UsersTab({ apiUrl, currentUser }) {
  const [users, setUsers] = useState([]);
  const [people, setPeople] = useState([]);
  const [form, setForm] = useState({ email: '', name: '', role: 'salesperson', person_id: '' });
  const [secret, setSecret] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await listUsers(apiUrl);
      setUsers(data?.items || []);
    } catch (err) {
      toast.error(errText(err, 'Could not load logins'));
    }
  }, [apiUrl]);

  useEffect(() => {
    load();
    fetchRoster(apiUrl)
      .then((r) => setPeople(Array.isArray(r) ? r : r?.items || r?.people || []))
      .catch(() => setPeople([]));
  }, [apiUrl, load]);

  const needsPerson = form.role === 'manager' || form.role === 'salesperson';
  const eligible = people.filter((p) => (form.role === 'manager' ? p.role === 'manager' : p.receives_leads));

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const data = await createUser(apiUrl, { ...form, person_id: needsPerson ? form.person_id : null });
      setSecret({ email: data.user.email, password: data.temporary_password });
      setForm({ email: '', name: '', role: 'salesperson', person_id: '' });
      load();
    } catch (err) {
      toast.error(errText(err, 'Could not create the login'));
    } finally {
      setBusy(false);
    }
  };

  const change = async (user, body, message) => {
    try {
      await updateUser(apiUrl, user.id, body);
      toast.success(message);
      load();
    } catch (err) {
      toast.error(errText(err, 'Could not update the login'));
    }
  };

  const reset = async (user) => {
    if (!window.confirm(`Reset the password for ${user.email}? They will be signed out.`)) return;
    try {
      const data = await resetUserPassword(apiUrl, user.id);
      setSecret({ email: user.email, password: data.temporary_password });
      load();
    } catch (err) {
      toast.error(errText(err, 'Could not reset the password'));
    }
  };

  return (
    <div className="users-tab" data-testid="users-tab">
      <h2>Team &amp; Access</h2>
      <p>Each person signs in with their email. A new or reset login gets a one-time password and must choose their own at first sign-in.</p>

      {secret ? (
        <div className="users-secret" role="status" data-testid="users-secret">
          Give <strong>{secret.email}</strong> this one-time password. It is shown only now:
          <code data-testid="users-secret-value"> {secret.password} </code>
          <button type="button" onClick={() => setSecret(null)}>Done</button>
        </div>
      ) : null}

      <form onSubmit={submit} className="users-form" data-testid="users-form">
        <input type="email" placeholder="Email" value={form.email} required data-testid="user-email"
          onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <input type="text" placeholder="Name" value={form.name} data-testid="user-name"
          onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <select value={form.role} data-testid="user-role" onChange={(e) => setForm({ ...form, role: e.target.value, person_id: '' })}>
          {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
        </select>
        {needsPerson ? (
          <select value={form.person_id} required data-testid="user-person" onChange={(e) => setForm({ ...form, person_id: e.target.value })}>
            <option value="">Which person in the team list?</option>
            {eligible.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        ) : null}
        <button type="submit" disabled={busy} data-testid="user-create">Create login</button>
      </form>

      <table className="users-table">
        <thead>
          <tr><th>Email</th><th>Name</th><th>Role</th><th>Person</th><th>Status</th><th /></tr>
        </thead>
        <tbody>
          {users.map((u) => {
            const self = u.email === currentUser?.username;
            return (
              <tr key={u.id} data-testid={`user-row-${u.email}`}>
                <td>{u.email}</td>
                <td>{u.name}</td>
                <td>{ROLE_LABELS[u.role] || u.role}</td>
                <td>{u.person_id || '—'}</td>
                <td>{u.active ? (u.must_change_password ? 'Waiting for first sign-in' : 'Active') : 'Deactivated'}</td>
                <td>
                  <button type="button" onClick={() => reset(u)}>Reset password</button>
                  {!self ? (
                    <button type="button" onClick={() => change(u, { active: !u.active }, u.active ? 'Login deactivated' : 'Login reactivated')}>
                      {u.active ? 'Deactivate' : 'Reactivate'}
                    </button>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
